import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Redirect, useLocation, useParams } from 'react-router-dom'

import { fetchAuditForm, submitAuditForm } from 'api'
import { navigationPaths } from 'app/config'
import { isUserAuthenticated } from 'app/helpers'
import { AuditQuestions } from 'app/pages/Audits/VisitAudits/AuditQuestions'

/**
 * New Centre Audit / Housekeeping Audit, opened in its own tab from the audit
 * pages (Satya, 2 Nov). Built like the issue form: no sidebar, phone-first, a
 * login needed. The link fixes the centre and the account gives the observer,
 * so neither is asked for here.
 */
const KINDS = {
  centre: { apiKind: 'centreVisitAudits', title: 'Centre Audit' },
  housekeeping: {
    apiKind: 'hkAudits',
    title: 'Housekeeping Audit',
    staffFields: true
  }
}

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: '#f3efe8',
    padding: theme.spacing(2, 1.5, 6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  stack: { width: '100%', maxWidth: 720 },
  card: {
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #e3ddd3',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.75)
  },
  brand: { color: '#F25E35', fontWeight: 800, fontSize: 15, lineHeight: 1 },
  title: { marginTop: theme.spacing(0.5), fontWeight: 600 },
  meta: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  },
  centreChip: { background: '#fdece5', color: '#b23c17', fontWeight: 700 },
  note: { fontSize: 13, color: '#5f5a52' },
  error: {
    background: '#fdecea',
    color: '#a4231a',
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    fontSize: 13.5,
    marginBottom: theme.spacing(1.75)
  },
  submitBtn: {
    background: '#f4511e',
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    '&:hover': { background: '#d84315' },
    '&:disabled': { background: '#e0d8cd', color: '#fff' }
  }
}))

const blankValues = (staffFields) => ({
  audit_date: dayjs().format('YYYY-MM-DD'),
  answers: {},
  comments: staffFields ? '' : {},
  ...(staffFields ? { staff_name: '', grade: '' } : {})
})

const errorText = (error, fallback) => {
  const detail = error && error.response && error.response.data
  return (detail && (detail.detail || detail.message)) || fallback
}

const AuditForm = () => {
  const classes = useStyles()
  const location = useLocation()
  const { kind, centre } = useParams()
  const config = KINDS[kind]
  const staffFields = Boolean(config && config.staffFields)
  const loggedIn = isUserAuthenticated()

  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [values, setValues] = useState(() => blankValues(staffFields))
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!loggedIn || !config) {
      setLoading(false)
      return undefined
    }
    let active = true
    fetchAuditForm(config.apiKind, centre)
      .then((data) => active && setForm(data))
      .catch(
        (error) =>
          active && setLoadError(errorText(error, 'Could not open this form.'))
      )
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [loggedIn, config, centre])

  if (!loggedIn) {
    return (
      <Redirect
        to={{ pathname: navigationPaths.login, state: { from: location } }}
      />
    )
  }

  const message = !config ? 'This link is not valid.' : loadError

  const setField = (key) => (e) => {
    const { value } = e.target
    setValues((v) => ({ ...v, [key]: value }))
  }
  const setAnswer = (key) => (e) => {
    const { value } = e.target
    setValues((v) => ({ ...v, answers: { ...v.answers, [key]: value } }))
  }
  const setSectionComment = (key) => (e) => {
    const { value } = e.target
    setValues((v) => ({ ...v, comments: { ...v.comments, [key]: value } }))
  }

  const submit = async () => {
    const left = form.sections
      .flatMap((s) => s.questions)
      .filter((q) => !values.answers[q.key]).length
    if (!values.audit_date || (staffFields && !values.staff_name.trim())) {
      setSaveError(
        staffFields
          ? 'Date and Housekeeping staff name are required.'
          : 'Date is required.'
      )
      return
    }
    if (left) {
      setSaveError(`${left} question${left > 1 ? 's' : ''} still to answer.`)
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      await submitAuditForm(config.apiKind, centre, values)
      setDone(true)
    } catch (error) {
      setSaveError(errorText(error, 'Could not save the audit.'))
    } finally {
      setSaving(false)
    }
  }

  const fileAnother = () => {
    setValues(blankValues(staffFields))
    setDone(false)
    window.scrollTo(0, 0)
  }

  if (loading) {
    return (
      <div className={classes.page}>
        <CircularProgress size={28} />
      </div>
    )
  }

  if (message) {
    return (
      <div className={classes.page}>
        <div className={classes.stack}>
          <div className={classes.error}>{message}</div>
        </div>
      </div>
    )
  }

  const header = (
    <div className={classes.card}>
      <div className={classes.brand}>openhouse</div>
      <Typography variant="h5" className={classes.title}>
        {config.title}
      </Typography>
      <div className={classes.meta}>
        <Chip
          size="small"
          label={form.centre_name}
          className={classes.centreChip}
        />
        <span className={classes.note}>Observer: {form.observer}</span>
      </div>
    </div>
  )

  if (done) {
    return (
      <div className={classes.page}>
        <div className={classes.stack}>
          {header}
          <div className={classes.card}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={2}>
              <CheckCircleIcon style={{ color: '#2d9d63' }} />
              <Typography>Audit saved.</Typography>
            </Box>
            <Button variant="outlined" onClick={fileAnother}>
              File another
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.page}>
      <div className={classes.stack}>
        {header}
        <div className={classes.card}>
          <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <TextField
              type="date"
              label="Date"
              required
              value={values.audit_date}
              onChange={setField('audit_date')}
              InputLabelProps={{ shrink: true }}
            />
            {staffFields && (
              <>
                <TextField
                  label="Housekeeping staff name"
                  required
                  helperText="Write All if you observed the whole housekeeping team"
                  value={values.staff_name}
                  onChange={setField('staff_name')}
                />
                <TextField
                  label="Grade"
                  value={values.grade}
                  onChange={setField('grade')}
                />
              </>
            )}
          </Box>
        </div>
        <div className={classes.card}>
          <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <AuditQuestions
              sections={form.sections}
              answers={values.answers}
              comments={values.comments}
              sectionComments={!staffFields}
              onAnswer={setAnswer}
              onSectionComment={setSectionComment}
            />
            {staffFields && (
              <TextField
                label="Comments"
                multiline
                minRows={2}
                value={values.comments}
                onChange={setField('comments')}
              />
            )}
          </Box>
        </div>
        {saveError && <div className={classes.error}>{saveError}</div>}
        <div className={classes.card}>
          <Button
            className={classes.submitBtn}
            disabled={saving}
            onClick={submit}
          >
            {saving ? 'Saving…' : 'Save audit'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AuditForm
